import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Search, 
  Plus, 
  User, 
  Phone, 
  Mail,
  Calendar,
  ChevronRight,
  FileText,
  Eye,
  Edit,
  Cake,
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PatientsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [newPatient, setNewPatient] = useState({
    full_name: '',
    phone: '',
    email: '',
    birth_date: '',
    sex: '',
    alternate_phone: '',
    referral_source: '',
    notes: ''
  });
  
  // Bulk import states
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  const PAGE_SIZE = 100;

  const fetchPatients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/patients/paginated`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, page_size: PAGE_SIZE, ...(searchTerm ? { search: searchTerm } : {}) }
      });
      setPatients(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotalPatients(response.data.total);
    } catch (error) {
      console.error('Error fetching patients:', error);
    } finally {
      setLoading(false);
    }
  }, [token, page, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(fetchPatients, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchPatients]);

  const handleCreatePatient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/patients`, newPatient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowNew(false);
      setNewPatient({ full_name: '', phone: '', email: '', birth_date: '', sex: '', alternate_phone: '', referral_source: '', notes: '' });
      fetchPatients();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear paciente');
    }
  };

  const handleEditPatient = (patient) => {
    setEditingPatient({
      id: patient.id,
      full_name: patient.full_name || '',
      phone: patient.phone || '',
      email: patient.email || '',
      birth_date: patient.birth_date || '',
      sex: patient.sex || '',
      alternate_phone: patient.alternate_phone || '',
      referral_source: patient.referral_source || '',
      notes: patient.notes || ''
    });
    setShowEdit(true);
  };

  const handleUpdatePatient = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/patients/${editingPatient.id}`, editingPatient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEdit(false);
      setEditingPatient(null);
      fetchPatients();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar paciente');
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Download bulk import template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/patients/bulk/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_importacion_pacientes.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('Error al descargar la plantilla');
    }
  };

  // Handle file selection for bulk import
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Por favor seleccione un archivo Excel (.xlsx o .xls)');
        return;
      }
      setBulkFile(file);
      setImportResult(null);
    }
  };

  // Process bulk import
  const handleBulkImport = async () => {
    if (!bulkFile) {
      toast.error('Por favor seleccione un archivo');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', bulkFile);

      const response = await axios.post(`${API}/patients/bulk/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} pacientes importados exitosamente`);
        fetchPatients();
      }
      
      if (response.data.errors > 0) {
        toast.warning(`${response.data.errors} filas con errores`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar pacientes');
      setImportResult(null);
    } finally {
      setImporting(false);
    }
  };

  // Close bulk import modal
  const closeBulkImportModal = () => {
    setShowBulkImport(false);
    setBulkFile(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-dots flex gap-2">
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="patients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#0B3B71]">Pacientes</h1>
          <p className="text-slate-500 mt-1">Gestiona la información de tus pacientes</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowBulkImport(true)}
            className="border-[#0891B2] text-[#0891B2] hover:bg-[#0891B2]/10"
            data-testid="bulk-import-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            Importar
          </Button>
          <Button 
            onClick={() => setShowNew(true)}
            className="bg-[#0891B2] hover:bg-[#0e7490]"
            data-testid="new-patient-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Paciente
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar por nombre o teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-patients"
            />
          </div>
        </CardContent>
      </Card>

      {/* Patients List - Table Format */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {patients.length === 0 ? (
            <div className="py-12 text-center">
              <User className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No se encontraron pacientes</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600 hidden lg:table-cell w-24">Código</th>
                    <th className="text-left p-4 font-medium text-slate-600">Nombre</th>
                    <th className="text-left p-4 font-medium text-slate-600 hidden sm:table-cell">Teléfono</th>
                    <th className="text-left p-4 font-medium text-slate-600 hidden md:table-cell">Fecha Nac.</th>
                    <th className="text-left p-4 font-medium text-slate-600 hidden md:table-cell">Edad</th>
                    <th className="text-left p-4 font-medium text-slate-600 hidden lg:table-cell">Citas</th>
                    <th className="text-center p-4 font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {patients.map((patient) => (
                    <tr 
                      key={patient.id} 
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`patient-row-${patient.id}`}
                    >
                      <td className="p-4 hidden lg:table-cell">
                        <span className="font-mono text-sm text-slate-600 bg-slate-100 px-2 py-1 rounded">
                          {patient.codigo_paciente || '-'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#0891B2]/10 flex items-center justify-center flex-shrink-0">
                            <User className="h-4 w-4 text-[#0891B2]" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{patient.full_name}</p>
                            <p className="text-sm text-slate-500 sm:hidden">{patient.phone}</p>
                            {/* Show code on mobile */}
                            {patient.codigo_paciente && (
                              <p className="text-xs text-slate-400 lg:hidden font-mono">{patient.codigo_paciente}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 hidden sm:table-cell">
                        <div className="flex items-center gap-1 text-slate-600">
                          <Phone className="h-3 w-3" />
                          <span>{patient.phone}</span>
                        </div>
                      </td>
                      <td className="p-4 hidden md:table-cell">
                        {patient.birth_date ? (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Cake className="h-3 w-3" />
                            <span>{format(new Date(patient.birth_date + 'T12:00:00'), "dd/MM/yyyy")}</span>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-4 hidden md:table-cell">
                        {patient.birth_date ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            {calculateAge(patient.birth_date)} años
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-4 hidden lg:table-cell">
                        <div className="flex items-center gap-1 text-slate-600">
                          <Calendar className="h-3 w-3" />
                          <span>{patient.total_appointments} citas</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/patients/${patient.id}/evaluacion-kanban`)}
                            className="h-8 w-8 p-0 text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                            title="Expediente Oftalmológico"
                            data-testid={`patient-evaluation-${patient.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditPatient(patient)}
                            className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            title="Modificar Paciente"
                            data-testid={`patient-edit-${patient.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/patients/${patient.id}`)}
                            className="h-8 w-8 p-0 text-slate-600 hover:text-slate-700 hover:bg-slate-100"
                            title="Ver Perfil"
                            data-testid={`patient-profile-${patient.id}`}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-t" data-testid="patients-pagination">
            <p className="text-sm text-slate-500" data-testid="patients-pagination-info">
              {totalPatients === 0
                ? '0 pacientes'
                : `Mostrando ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, totalPatients)} de ${totalPatients} pacientes`}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="patients-prev-page">
                Anterior
              </Button>
              <span className="text-sm text-slate-600" data-testid="patients-page-indicator">Página {page} de {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="patients-next-page">
                Siguiente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* New Patient Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71]">Nuevo Paciente</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePatient} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre completo</Label>
              <Input
                value={newPatient.full_name}
                onChange={(e) => setNewPatient({ ...newPatient, full_name: e.target.value })}
                required
                data-testid="new-patient-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha de Nacimiento</Label>
                <Input
                  type="date"
                  value={newPatient.birth_date}
                  onChange={(e) => setNewPatient({ ...newPatient, birth_date: e.target.value })}
                  data-testid="new-patient-birth-date"
                />
                {newPatient.birth_date && (
                  <Badge variant="outline" className="text-xs">
                    {(() => {
                      const today = new Date();
                      const birth = new Date(newPatient.birth_date);
                      let age = today.getFullYear() - birth.getFullYear();
                      const monthDiff = today.getMonth() - birth.getMonth();
                      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
                        age--;
                      }
                      return `${age} años`;
                    })()}
                  </Badge>
                )}
              </div>
              <div className="space-y-2">
                <Label>Sexo</Label>
                <Select
                  value={newPatient.sex}
                  onValueChange={(value) => setNewPatient({ ...newPatient, sex: value })}
                >
                  <SelectTrigger data-testid="new-patient-sex">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="masculino">Masculino</SelectItem>
                    <SelectItem value="femenino">Femenino</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={newPatient.phone}
                  onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })}
                  required
                  placeholder="5555-5555"
                  data-testid="new-patient-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono Alterno</Label>
                <Input
                  value={newPatient.alternate_phone}
                  onChange={(e) => setNewPatient({ ...newPatient, alternate_phone: e.target.value })}
                  placeholder="Opcional"
                  data-testid="new-patient-alternate-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email (opcional)</Label>
              <Input
                type="email"
                value={newPatient.email}
                onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })}
                data-testid="new-patient-email"
              />
            </div>

            <div className="space-y-2">
              <Label>¿Cómo se enteró de nosotros?</Label>
              <Select
                value={newPatient.referral_source}
                onValueChange={(value) => setNewPatient({ ...newPatient, referral_source: value })}
              >
                <SelectTrigger data-testid="new-patient-referral">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recomendacion">Recomendación de otro paciente</SelectItem>
                  <SelectItem value="redes_sociales">Redes Sociales</SelectItem>
                  <SelectItem value="google">Google / Búsqueda</SelectItem>
                  <SelectItem value="publicidad">Publicidad</SelectItem>
                  <SelectItem value="clinica_cercana">Clínica cercana</SelectItem>
                  <SelectItem value="seguro_medico">Seguro Médico</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={newPatient.notes}
                onChange={(e) => setNewPatient({ ...newPatient, notes: e.target.value })}
                rows={2}
                placeholder="Observaciones adicionales..."
                data-testid="new-patient-notes"
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowNew(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-[#0891B2] hover:bg-[#0e7490]" data-testid="create-patient-button">
                Crear Paciente
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Patient Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71]">Modificar Paciente</DialogTitle>
          </DialogHeader>
          {editingPatient && (
            <form onSubmit={handleUpdatePatient} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre completo</Label>
                <Input
                  value={editingPatient.full_name}
                  onChange={(e) => setEditingPatient({ ...editingPatient, full_name: e.target.value })}
                  required
                  data-testid="edit-patient-name"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha de Nacimiento</Label>
                  <Input
                    type="date"
                    value={editingPatient.birth_date}
                    onChange={(e) => setEditingPatient({ ...editingPatient, birth_date: e.target.value })}
                    data-testid="edit-patient-birth-date"
                  />
                  {editingPatient.birth_date && (
                    <Badge variant="outline" className="text-xs">
                      {calculateAge(editingPatient.birth_date)} años
                    </Badge>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Sexo</Label>
                  <Select
                    value={editingPatient.sex}
                    onValueChange={(value) => setEditingPatient({ ...editingPatient, sex: value })}
                  >
                    <SelectTrigger data-testid="edit-patient-sex">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="masculino">Masculino</SelectItem>
                      <SelectItem value="femenino">Femenino</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={editingPatient.phone}
                    onChange={(e) => setEditingPatient({ ...editingPatient, phone: e.target.value })}
                    required
                    placeholder="5555-5555"
                    data-testid="edit-patient-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono Alterno</Label>
                  <Input
                    value={editingPatient.alternate_phone}
                    onChange={(e) => setEditingPatient({ ...editingPatient, alternate_phone: e.target.value })}
                    placeholder="Opcional"
                    data-testid="edit-patient-alternate-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Email (opcional)</Label>
                <Input
                  type="email"
                  value={editingPatient.email}
                  onChange={(e) => setEditingPatient({ ...editingPatient, email: e.target.value })}
                  data-testid="edit-patient-email"
                />
              </div>

              <div className="space-y-2">
                <Label>¿Cómo se enteró de nosotros?</Label>
                <Select
                  value={editingPatient.referral_source}
                  onValueChange={(value) => setEditingPatient({ ...editingPatient, referral_source: value })}
                >
                  <SelectTrigger data-testid="edit-patient-referral">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recomendacion">Recomendación de otro paciente</SelectItem>
                    <SelectItem value="redes_sociales">Redes Sociales</SelectItem>
                    <SelectItem value="google">Google / Búsqueda</SelectItem>
                    <SelectItem value="publicidad">Publicidad</SelectItem>
                    <SelectItem value="clinica_cercana">Clínica cercana</SelectItem>
                    <SelectItem value="seguro_medico">Seguro Médico</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea
                  value={editingPatient.notes}
                  onChange={(e) => setEditingPatient({ ...editingPatient, notes: e.target.value })}
                  rows={2}
                  placeholder="Observaciones adicionales..."
                  data-testid="edit-patient-notes"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowEdit(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-[#0891B2] hover:bg-[#0e7490]" data-testid="update-patient-button">
                  Guardar Cambios
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Import Modal */}
      <Dialog open={showBulkImport} onOpenChange={closeBulkImportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71] flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Importación Masiva de Pacientes
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">Instrucciones:</h4>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Descargue la plantilla de Excel</li>
                <li>Complete los datos de los pacientes</li>
                <li>Suba el archivo completado</li>
              </ol>
            </div>

            {/* Download Template Button */}
            <Button 
              variant="outline" 
              onClick={handleDownloadTemplate}
              className="w-full border-green-500 text-green-700 hover:bg-green-50"
              data-testid="download-template-button"
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar Plantilla Excel
            </Button>

            {/* File Upload Area */}
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                bulkFile ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-[#0891B2]'
              }`}
            >
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                ref={fileInputRef}
                className="hidden"
                id="bulk-file-input"
                data-testid="bulk-file-input"
              />
              <label htmlFor="bulk-file-input" className="cursor-pointer">
                {bulkFile ? (
                  <div className="flex flex-col items-center gap-2">
                    <CheckCircle className="h-10 w-10 text-green-500" />
                    <span className="font-medium text-green-700">{bulkFile.name}</span>
                    <span className="text-sm text-slate-500">Clic para cambiar archivo</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="h-10 w-10 text-slate-400" />
                    <span className="font-medium text-slate-600">Seleccionar archivo Excel</span>
                    <span className="text-sm text-slate-400">o arrastre y suelte aquí</span>
                  </div>
                )}
              </label>
            </div>

            {/* Import Results */}
            {importResult && (
              <div className={`rounded-lg p-4 ${
                importResult.errors === 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {importResult.errors === 0 ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  )}
                  <span className="font-medium">Resultado de la importación</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-2xl font-bold text-slate-700">{importResult.total_rows}</p>
                    <p className="text-slate-500">Total filas</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-2xl font-bold text-green-600">{importResult.imported}</p>
                    <p className="text-slate-500">Importados</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-2xl font-bold text-red-600">{importResult.errors}</p>
                    <p className="text-slate-500">Errores</p>
                  </div>
                </div>
                
                {/* Error Details */}
                {importResult.error_details && importResult.error_details.length > 0 && (
                  <div className="mt-3 max-h-32 overflow-y-auto">
                    <p className="text-xs font-medium text-slate-600 mb-1">Detalles de errores:</p>
                    {importResult.error_details.map((err, idx) => (
                      <div key={idx} className="text-xs text-red-600 flex gap-2">
                        <XCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span>Fila {err.row}: {err.error}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeBulkImportModal}>
              {importResult ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!importResult && (
              <Button 
                onClick={handleBulkImport}
                disabled={!bulkFile || importing}
                className="bg-[#0891B2] hover:bg-[#0e7490]"
                data-testid="import-patients-button"
              >
                {importing ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Importar Pacientes
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientsPage;
